/**
 * Meteo da Open-Meteo (gratuito, senza chiave API, CORS abilitato) e regole
 * di consiglio contestuale.
 *
 * Principio: il meteo INFORMA e PROPONE, non decide. Il percorso consigliato
 * resta geometrico (ombra vera); qui si aggiungono avvisi con un'azione a un
 * tap («cielo coperto → usa il più breve», «UV 9 → massima ombra»). Non blocca
 * mai il calcolo: se il servizio non risponde, l'app funziona come prima.
 */

import type { LatLng } from './geo';
import type { DaylightInfo } from './sun';

const API = 'https://api.open-meteo.com/v1/forecast';

export interface HourlyWeather {
  timeMs: number;
  tempC: number;
  apparentC: number;
  cloudCoverPct: number;
  uvIndex: number;
  precipProbPct: number;
  /** codice WMO (0 sereno … 99 temporale con grandine) */
  weatherCode: number;
  isDay: boolean;
}

export interface WeatherForecast {
  lat: number;
  lng: number;
  fetchedAt: number;
  hours: HourlyWeather[];
}

interface OpenMeteoResponse {
  hourly?: {
    time: number[];
    temperature_2m: Array<number | null>;
    apparent_temperature: Array<number | null>;
    cloud_cover: Array<number | null>;
    uv_index: Array<number | null>;
    precipitation_probability: Array<number | null>;
    weather_code: Array<number | null>;
    is_day: Array<number | null>;
  };
}

export async function fetchWeather(
  ll: LatLng,
  opts: { signal?: AbortSignal; fetchImpl?: typeof fetch; days?: number } = {},
): Promise<WeatherForecast> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const params = new URLSearchParams({
    latitude: ll.lat.toFixed(4),
    longitude: ll.lng.toFixed(4),
    hourly: [
      'temperature_2m',
      'apparent_temperature',
      'cloud_cover',
      'uv_index',
      'precipitation_probability',
      'weather_code',
      'is_day',
    ].join(','),
    timeformat: 'unixtime',
    forecast_days: String(opts.days ?? 7),
  });
  const res = await fetchImpl(`${API}?${params.toString()}`, { signal: opts.signal ?? null });
  if (!res.ok) throw new Error(`Open-Meteo HTTP ${res.status}`);
  const json = (await res.json()) as OpenMeteoResponse;
  return parseOpenMeteo(json, ll);
}

export function parseOpenMeteo(json: OpenMeteoResponse, ll: LatLng): WeatherForecast {
  const h = json.hourly;
  const hours: HourlyWeather[] = [];
  if (h?.time) {
    for (let i = 0; i < h.time.length; i++) {
      const temp = h.temperature_2m[i];
      if (temp === null || temp === undefined) continue;
      hours.push({
        timeMs: h.time[i] * 1000,
        tempC: temp,
        apparentC: h.apparent_temperature[i] ?? temp,
        cloudCoverPct: h.cloud_cover[i] ?? 0,
        uvIndex: h.uv_index[i] ?? 0,
        precipProbPct: h.precipitation_probability[i] ?? 0,
        weatherCode: h.weather_code[i] ?? 0,
        isDay: (h.is_day[i] ?? 1) === 1,
      });
    }
  }
  return { lat: ll.lat, lng: ll.lng, fetchedAt: Date.now(), hours };
}

/** L'ora di previsione più vicina a `tMs`, se entro 90 minuti. */
export function weatherAt(f: WeatherForecast | null, tMs: number): HourlyWeather | null {
  if (!f || f.hours.length === 0) return null;
  let best: HourlyWeather | null = null;
  let bestDelta = Infinity;
  for (const h of f.hours) {
    const d = Math.abs(h.timeMs - tMs);
    if (d < bestDelta) {
      bestDelta = d;
      best = h;
    }
  }
  return bestDelta <= 90 * 60_000 ? best : null;
}

export type WeatherIcon =
  | 'sun'
  | 'partly'
  | 'cloud'
  | 'fog'
  | 'rain'
  | 'snow'
  | 'storm'
  | 'moon'
  | 'sunset'
  | 'uv'
  | 'heat'
  | 'cold';

/** Codici WMO → etichetta italiana e icona. */
export function describeWeatherCode(code: number): { label: string; icon: WeatherIcon } {
  if (code === 0) return { label: 'Sereno', icon: 'sun' };
  if (code === 1) return { label: 'Prevalentemente sereno', icon: 'sun' };
  if (code === 2) return { label: 'Parzialmente nuvoloso', icon: 'partly' };
  if (code === 3) return { label: 'Coperto', icon: 'cloud' };
  if (code === 45 || code === 48) return { label: 'Nebbia', icon: 'fog' };
  if (code >= 51 && code <= 57) return { label: 'Pioviggine', icon: 'rain' };
  if (code >= 61 && code <= 67) return { label: 'Pioggia', icon: 'rain' };
  if (code >= 71 && code <= 77) return { label: 'Neve', icon: 'snow' };
  if (code >= 80 && code <= 82) return { label: 'Rovesci', icon: 'rain' };
  if (code === 85 || code === 86) return { label: 'Rovesci di neve', icon: 'snow' };
  if (code >= 95) return { label: 'Temporale', icon: 'storm' };
  return { label: 'Variabile', icon: 'partly' };
}

export type PresetKey = 'fast' | 'auto' | 'max';

export interface WeatherAdvice {
  level: 'warn' | 'tip' | 'info';
  icon: WeatherIcon;
  text: string;
  action?: { label: string; preset: PresetKey };
}

export interface AdviceContext {
  depart: HourlyWeather | null;
  arrive: HourlyWeather | null;
  daylight: DaylightInfo;
  departMs: number;
  arrivalMs: number;
  chosenShadePct: number;
  chosenIsShortest: boolean;
  /** formattazione oraria (dipende dal fuso del dispositivo) */
  fmtTime: (ms: number) => string;
}

const isStorm = (code: number) => code >= 95;
const isRainy = (code: number) => (code >= 51 && code <= 67) || (code >= 80 && code <= 82);

/**
 * Consigli contestuali, ordinati per importanza (max 3). Ogni regola è
 * indipendente: il meteo mancante (null) disattiva solo le regole meteo,
 * quelle astronomiche funzionano sempre.
 */
export function buildAdvice(ctx: AdviceContext): WeatherAdvice[] {
  const out: WeatherAdvice[] = [];
  const { depart, arrive, daylight, departMs, arrivalMs, chosenShadePct, chosenIsShortest } = ctx;
  const t = ctx.fmtTime;

  // ---- astronomia: vale sempre, anche senza meteo ----
  if (daylight.kind === 'polarNight') {
    out.push({
      level: 'info',
      icon: 'moon',
      text: 'Qui oggi il sole non sorge: è tutto in ombra, e il percorso più breve è anche il migliore.',
    });
  } else if (daylight.kind === 'polarDay') {
    out.push({
      level: 'info',
      icon: 'sun',
      text: 'Qui oggi il sole non tramonta: l’ombra conta a qualunque ora, anche di notte.',
    });
  } else if (!daylight.isDay) {
    const rise = daylight.nextSunrise;
    const riseBeforeArrival = rise !== undefined && rise < arrivalMs;
    out.push({
      level: 'info',
      icon: 'moon',
      text: riseBeforeArrival
        ? `Parti col sole sotto l’orizzonte; sorge alle ${t(rise)}, prima del tuo arrivo (${t(arrivalMs)}): l’ultimo tratto sarà con la luce.`
        : `Parti col sole sotto l’orizzonte${rise !== undefined ? ` (alba alle ${t(rise)})` : ''}: tutto è in ombra, quindi vale il percorso più breve.`,
    });
  } else if (daylight.nextSunset !== undefined && daylight.nextSunset < arrivalMs) {
    out.push({
      level: 'tip',
      icon: 'sunset',
      text: `Il sole tramonta alle ${t(daylight.nextSunset)} e arrivi alle ${t(arrivalMs)}: l’ultimo tratto sarà al buio (e all’ombra).`,
    });
  } else if (
    daylight.nextSunset !== undefined &&
    daylight.nextSunset - departMs < 40 * 60_000 &&
    !chosenIsShortest
  ) {
    out.push({
      level: 'tip',
      icon: 'sunset',
      text: `Tramonto alle ${t(daylight.nextSunset)}: sole bassissimo e ombre lunghe ovunque, la deviazione rende poco.`,
      action: { label: 'Usa il più breve', preset: 'fast' },
    });
  }

  // ---- meteo alla partenza ----
  if (depart && daylight.isDay) {
    if (isStorm(depart.weatherCode)) {
      out.push({
        level: 'warn',
        icon: 'storm',
        text: `Temporali previsti (pioggia ${depart.precipProbPct}%): forse è meglio rimandare la passeggiata.`,
      });
    } else if (depart.precipProbPct >= 60 || isRainy(depart.weatherCode)) {
      out.push({
        level: 'tip',
        icon: 'rain',
        text: `Probabile pioggia (${depart.precipProbPct}%): portici e gallerie lungo il percorso valgono doppio.`,
      });
    }

    if (depart.cloudCoverPct >= 85 && !isStorm(depart.weatherCode)) {
      out.push({
        level: 'tip',
        icon: 'cloud',
        text: `Cielo coperto (${depart.cloudCoverPct}%): oggi l’ombra conta poco.`,
        action: chosenIsShortest ? undefined : { label: 'Usa il più breve', preset: 'fast' },
      });
    }

    if (depart.uvIndex >= 8) {
      out.push({
        level: 'warn',
        icon: 'uv',
        text: `UV molto alto (${depart.uvIndex.toFixed(0)}): stare all’ombra oggi non è solo una comodità.`,
        action: chosenShadePct < 75 ? { label: 'Massima ombra', preset: 'max' } : undefined,
      });
    } else if (depart.uvIndex >= 6 && depart.cloudCoverPct < 60) {
      out.push({
        level: 'tip',
        icon: 'uv',
        text: `UV alto (${depart.uvIndex.toFixed(0)}): vale la pena stare al riparo.`,
        action: chosenShadePct < 60 ? { label: 'Massima ombra', preset: 'max' } : undefined,
      });
    }

    if (depart.apparentC >= 33) {
      out.push({
        level: 'warn',
        icon: 'heat',
        text: `Caldo intenso (${Math.round(depart.apparentC)}°C percepiti): massima ombra e acqua con te.`,
        action: chosenShadePct < 75 ? { label: 'Massima ombra', preset: 'max' } : undefined,
      });
    } else if (depart.apparentC >= 29) {
      out.push({
        level: 'tip',
        icon: 'heat',
        text: `Fa caldo (${Math.round(depart.apparentC)}°C percepiti): l’ombra oggi si sente.`,
        action: chosenShadePct < 60 ? { label: 'Massima ombra', preset: 'max' } : undefined,
      });
    } else if (depart.apparentC <= 6 && depart.cloudCoverPct < 40) {
      out.push({
        level: 'tip',
        icon: 'cold',
        text: `Freddo e sereno (${Math.round(depart.apparentC)}°C): oggi forse preferisci il sole.`,
        action: chosenIsShortest ? undefined : { label: 'Usa il più breve', preset: 'fast' },
      });
    }
  }

  // ---- cambia all'arrivo ----
  if (depart && arrive && arrive.timeMs !== depart.timeMs) {
    if (arrive.precipProbPct >= 60 && depart.precipProbPct < 40) {
      out.push({
        level: 'tip',
        icon: 'rain',
        text: `All’arrivo la pioggia è più probabile (${arrive.precipProbPct}%): meglio non attardarsi.`,
      });
    }
  }

  const rank = { warn: 0, tip: 1, info: 2 };
  return out.sort((a, b) => rank[a.level] - rank[b.level]).slice(0, 3);
}
